import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  validateString,
  validationLength,
  validationType,
  validationMessages,
} from "../../actions/validations";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import { SnCPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { golfTooltipHeaders } from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";

const PERIODS_OPTIONS = ["12", "13"];
const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;

const SnCFiscalPeriods = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [startingYear, setStartingYear] = useState<string | null>(null);
  const [numberOfPeriods, setNumberOfPeriods] = useState<string>("12");
  const [periodStartDate, setPeriodStartDate] = useState<string | null>(null);
  const [periodEndDate, setPeriodEndDate] = useState<string | null>(null);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [startingYearErr, setStartingYearErr] = useState("");
  const [periodsErr, setPeriodsErr] = useState("");
  const [startDateErr, setStartDateErr] = useState("");
  const [endDateErr, setEndDateErr] = useState("");

  useEffect(() => {
    fieldValues && setItems(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const payload = {
    "Starting Year": startingYear,
    "# of Periods": numberOfPeriods,
    "Period Start Date": periodStartDate,
    "Period End Date": periodEndDate,
  };

  const validateInput = (): boolean => {
    setStartingYearErr("");
    setPeriodsErr("");
    setStartDateErr("");
    setEndDateErr("");
    if (!startingYear) {
      setStartingYearErr(
        "Starting Year" + validationMessages.sncMandatorySuffix,
      );
    }
    if (!numberOfPeriods) {
      setPeriodsErr("# of Periods" + validationMessages.sncMandatorySuffix);
    }
    if (!periodStartDate) {
      setStartDateErr(
        "Period Start Date" + validationMessages.sncMandatorySuffix,
      );
    }
    if (!periodEndDate) {
      setEndDateErr("Period End Date" + validationMessages.sncMandatorySuffix);
    }
    if (
      !startingYear ||
      !numberOfPeriods ||
      !periodStartDate ||
      !periodEndDate
    ) {
      setAlertMessage(validationMessages.sncAllMandatory);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (
      !validateString(
        startingYear,
        validationLength.originCode,
        validationType.CODE,
      )
    ) {
      setStartingYearErr(validationMessages.originCode);
      return false;
    }
    if (
      !validateString(
        numberOfPeriods,
        validationLength.originCode,
        validationType.CODE,
      )
    ) {
      setPeriodsErr(validationMessages.originCode);
      return false;
    }
    if (!isoDateRegex.test(periodStartDate)) {
      setStartDateErr(validationMessages.isoDate);
      return false;
    }
    if (!isoDateRegex.test(periodEndDate)) {
      setEndDateErr(validationMessages.isoDate);
      return false;
    }
    return true;
  };

  const updateItem = () => {
    if (!validateInput()) {
      return;
    }
    const otherItems = (items ?? []).filter(
      (item: any) => item.id !== selectedId,
    );
    if (checkDuplicate(otherItems, "Starting Year", startingYear)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev) =>
      (prev ?? []).map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SnCPropertyEnum.fiscalPeriods} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateInput()) {
      return;
    }
    if (checkDuplicate(items ?? [], "Starting Year", startingYear)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: (prev?.length ?? 0) + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setStartingYearErr("");
      setPeriodsErr("");
      setStartDateErr("");
      setEndDateErr("");
      setStartingYear(list["Starting Year"] ?? null);
      setNumberOfPeriods(list["# of Periods"] ?? "12");
      setPeriodStartDate(list["Period Start Date"] ?? null);
      setPeriodEndDate(list["Period End Date"] ?? null);
    } else {
      setStartingYear(null);
      setNumberOfPeriods("12");
      setPeriodStartDate(null);
      setPeriodEndDate(null);
      setStartingYearErr("");
      setPeriodsErr("");
      setStartDateErr("");
      setEndDateErr("");
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items ?? [], id));
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Fiscal Periods
            <Tooltip
              title={tooltipComponent(
                golfTooltipHeaders?.categoryGroup ?? "Fiscal Periods",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px">
          <Stack direction="column">
            <FormLabel component="legend">
              Starting Year
              <Tooltip title={tooltipComponent(golfTooltipHeaders.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <TextField
              required
              variant="outlined"
              size="small"
              value={startingYear ?? ""}
              error={!!startingYearErr}
              helperText={startingYearErr}
              onChange={(e) => {
                setStartingYear(e.target.value || null);
                setStartingYearErr("");
              }}
            />
          </Stack>
          <Stack direction="column">
            <FormLabel component="legend">
              # of Periods*
              <Tooltip title={tooltipComponent(golfTooltipHeaders.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <Autocomplete
              size="small"
              options={PERIODS_OPTIONS}
              value={numberOfPeriods}
              onChange={(_, v) => {
                setNumberOfPeriods(v ?? "12");
                setPeriodsErr("");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  variant="outlined"
                  error={!!periodsErr}
                  helperText={periodsErr}
                />
              )}
            />
          </Stack>
          <Stack direction="column">
            <FormLabel component="legend">
              Period Start Date*
              <Tooltip title={tooltipComponent(golfTooltipHeaders.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <TextField
              required
              variant="outlined"
              size="small"
              type="date"
              value={periodStartDate ?? ""}
              error={!!startDateErr}
              helperText={startDateErr}
              onChange={(e) => {
                setPeriodStartDate(e.target.value || null);
                setStartDateErr("");
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
          <Stack direction="column">
            <FormLabel component="legend">
              Period End Date
              <Tooltip title={tooltipComponent(golfTooltipHeaders.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <TextField
              required
              variant="outlined"
              size="small"
              type="date"
              value={periodEndDate ?? ""}
              error={!!endDateErr}
              helperText={endDateErr}
              onChange={(e) => {
                setPeriodEndDate(e.target.value || null);
                setEndDateErr("");
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Fiscal Period
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Fiscal Periods Available</Typography>
          {items?.length ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code="Starting Year"
              name="# of Periods"
            />
          ) : (
            <Typography variant="body2">No Fiscal Periods Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SnCFiscalPeriods;
